import { AntTag, ColumnsType, Text } from '@agro-club/agroclub-shared'
import useFormatDate from 'hooks/useFormatDate'
import { convertSortingToAntDFormat } from 'modules/domain/common/tableUtils'
import { ListRequestParams } from 'modules/domain/types'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import UserRoutes from 'views/pages/User/routes'
import { TableTextLink } from 'views/ui/TableTextLink/TableTextLink'
import SamplesRoutes from '../routes'
import { getSampleTimeLeft, statusColorMap } from '../helpers'
import { Coordinator } from 'modules/domain/user/types'
import { SampleForList, SampleReactionStatus, SampleStatus } from '../types'
import { labelQuantity } from 'modules/utils/numbers/formatQuantity'
import formatNumber from 'modules/utils/numbers/formatNumber'
import { CommmentIcon } from 'views/components/TooltipIcons/TooltipIcons'
import styled from 'styled-components'
import { date } from 'views/components/Table/renderers'

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const reactionStatusesColors = {
  [SampleReactionStatus.expired]: 'red',
  [SampleReactionStatus.norm]: 'blue',
  [SampleReactionStatus.paused]: 'orange',
  [SampleReactionStatus.reacted]: 'green',
}
export const useColumns = (sortParam: ListRequestParams['sort']): ColumnsType<SampleForList> => {
  const { t } = useTranslation('samples')
  const formatDate = useFormatDate()
  return useMemo(
    () => [
      {
        title: t('list.id'),
        width: 120,
        dataIndex: 'id',
        sorter: true,
        key: 'id',
        sortOrder: convertSortingToAntDFormat('id', String(sortParam)),
        render: (id) => (
          <TableTextLink id="title" to={generatePath(SamplesRoutes.Details, { id })}>
            {id}
          </TableTextLink>
        ),
      },
      {
        title: t('list.bidId'),
        width: 120,
        dataIndex: 'bid_id',
      },
      {
        title: t('list.status'),
        width: 236,
        dataIndex: 'status',
        render: (status: SampleStatus, row) => {
          let tag
          if (status === SampleStatus.in_progress && row.request_status) {
            tag = (
              <AntTag color={statusColorMap[status]}>{t(`sampleRequestStatusesForTags.${row.request_status}`)}</AntTag>
            )
          } else {
            tag = <AntTag color={statusColorMap[status]}>{t(`sampleStatus.${status}`)}</AntTag>
          }
          return (
            <StatusWrapper>
              {tag}
              {row.request_delay_comment && (
                <CommmentIcon comment={`${t('sampleDelay')}: ${row.request_delay_comment}`} />
              )}
            </StatusWrapper>
          )
        },
      },
      {
        title: t('list.reaction_status'),
        width: 220,
        dataIndex: 'reaction_status',
        render: (reaction_status) => {
          if (!reaction_status) return null
          return (
            <AntTag color={reactionStatusesColors[reaction_status]}>{t(`sampleReaction.${reaction_status}`)}</AntTag>
          )
        },
      },
      {
        title: t('list.timer'),
        width: 220,
        dataIndex: 'react_until',
        render: (react_until: string, record) => {
          let comment: ReactElement | null = null
          if (record.reaction_status === SampleReactionStatus.paused) {
            comment = (
              <Text typography="bodySmall" color="secondary">
                {record.pause_reason}
              </Text>
            )
          }
          const res = getSampleTimeLeft(record, t)
          if (!res) return null
          return (
            <div>
              <Text
                typography={res.delta > 0 ? 'bodyMedium' : 'accentMedium'}
                color={res.delta > 0 ? undefined : 'accentDestruct600'}
              >
                {res.text}
              </Text>
              {comment}
            </div>
          )
        },
      },
      {
        title: t('list.crop'),
        width: 220,
        dataIndex: 'product',
        render: (product) => product.title,
      },
      {
        title: labelQuantity(),
        width: 150,
        dataIndex: 'quantity',
        sorter: true,
        sortOrder: convertSortingToAntDFormat('quantity', String(sortParam)),
        align: 'right',
        render: (quantity) => formatNumber(quantity),
      },
      {
        title: t('list.forWhom'),
        width: 360,
        dataIndex: 'owner',
        render: (owner) => (
          <TableTextLink id="title" to={generatePath(UserRoutes.Details, { id: owner.id })}>
            {owner.full_name}
          </TableTextLink>
        ),
      },
      {
        title: t('list.dateCreation'),
        width: 150,
        dataIndex: 'created',
        sorter: true,
        sortOrder: convertSortingToAntDFormat('created', String(sortParam)),
        render: (created) => formatDate(created),
      },
      {
        title: t('list.publishedAt'),
        width: 150,
        dataIndex: 'published_at',
        sorter: true,
        sortOrder: convertSortingToAntDFormat('published_at', String(sortParam)),
        render: (published_at) => formatDate(published_at),
      },
      {
        title: t('sentToExportBotShort'),
        width: 150,
        dataIndex: 'sent_to_export_bot_date',
        sorter: true,
        sortOrder: convertSortingToAntDFormat('sent_to_export_bot_date', String(sortParam)),
        render: date,
      },
      {
        title: t('sentToLocalBotShort'),
        width: 150,
        dataIndex: 'sent_to_local_bot_date',
        sorter: true,
        sortOrder: convertSortingToAntDFormat('sent_to_local_bot_date', String(sortParam)),
        render: date,
      },
      {
        title: t('common:responsible'),
        width: 300,
        dataIndex: 'request_current_responsible',
        key: 'request_current_responsible',
        render: (coordinator?: Coordinator) => coordinator?.full_name,
      },
      {
        title: t('laboratory'),
        width: 120,
        dataIndex: 'laboratory',
        render: (lab_code) => (lab_code ? t(`samples:laboratories.${lab_code}`) : null),
      },
      {
        title: t('list.team'),
        width: 152,
        dataIndex: 'team',
      },
    ],
    [t, formatDate, sortParam],
  )
}
